import {Injectable} from '@angular/core';
import {ConfigService} from './config.service';
import {HttpClient, HttpParams} from '@angular/common/http';
import {CreateUserResponse, User} from '../model/user.model';
import {ContactInfos} from '../model/contact-infos.model';
import {ContactInfo} from '../model/contact-info.model';
import {EmailAddresses} from '../model/email-addresses.model';
import {EmailAddress} from '../model/email-address.model';
import { panExists } from '../model/panExists.model';

@Injectable()
export class UserService {
    private readonly httpClient: HttpClient;
    private readonly configService: ConfigService;

    constructor(httpClient: HttpClient, configService: ConfigService) {
        this.httpClient = httpClient;
        this.configService = configService;
    }

    async getUserLenderApplicationStatus(profileId: string, token: string): Promise<any> {
        const url = `${this.configService.baseUrl()}getCompleteProfileDetails`;
        const responseJson = await this.httpClient.post(url, {profile_id: profileId, token: token}).toPromise();
        if (responseJson['status_code'] === 201) {
            return responseJson;
        }
        throw new Error('Response for fetching loan and lender application did not have expected status_code.');
    }

    async getUserPanAndContactInfo(profileId: string, token: string): Promise<any> {
        const url = `${this.configService.baseUrl()}getProfileDetails`;
        const responseJson = await this.httpClient.post(url, {profile_id: profileId, token: token}).toPromise();
        if (responseJson['status_code'] === 201) {
            return responseJson;
        }
        throw new Error('Response for fetching user\'s PAN and contact info did not have expected status_code.');
    }

    async verifyAndRegisterEmail(profileId: string, emailId: string, verificationCode: string): Promise<void> {
        const emailUpdateUrl = `${this.configService.baseUrl()}user/${profileId}/register/email/verify`;
        const body = {
            email: emailId,
            verificationCode: verificationCode
        };
        await this.httpClient.post(emailUpdateUrl, body).toPromise();
    }

    async getProfileDetails(userId: string): Promise<User> {
        const url = `${this.configService.baseUrl()}user/${userId}`;
        return await this.httpClient.get<User>(url).toPromise();
    }

    public async getFilteredDetailsById(userId: string) {
        const url = `${this.configService.baseUrl()}user?requestedProfileId=${userId}`;
        return await this.httpClient.get<any>(url).toPromise();
    }

    async verifyPan(pan: string, name: string): Promise<string> {
        const phoneVerificationTokenUrl = `${this.configService.baseUrl()}user/verify-pan`;
        const body = {pan: pan, name: name};
        const responseBody = await this.httpClient.post<{ phoneVerificationRequestToken: string }>(phoneVerificationTokenUrl, body).toPromise();
        return responseBody.phoneVerificationRequestToken;
    }

    async requestForPhoneVerification(phoneNumber: string, phoneVerificationRequestToken: string): Promise<string> {
        const response = await this.httpClient.post<{ phoneVerificationToken: string }>(`${this.configService.baseUrl()}user/register/phone-number`, {
            phoneNumber,
            phoneVerificationRequestToken
        }).toPromise();
        return response.phoneVerificationToken;
    }

    async verifyPhoneForRegistration(otp: string, phoneVerificationToken: string): Promise<string> {
        const response = await this.httpClient.post<{ userCreationToken: string }>(`${this.configService.baseUrl()}user/register/phone-number/verify`, {
            otp,
            phoneVerificationToken
        }).toPromise();
        return response.userCreationToken;
    }

    async createUser(userCreationToken: string, referrerCode?: string, onboardedBy?: string, invitationId?: string): Promise<CreateUserResponse> {
        const requestBody: { userCreationToken: string, referredBy?: string, onBoardedBy?: string, invitationId?: string } = {userCreationToken};
        if (referrerCode) {
            requestBody.referredBy = referrerCode;
        }
        if (onboardedBy) {
            requestBody.onBoardedBy = onboardedBy;
        }

        if (invitationId) {
            requestBody.invitationId = invitationId;
        }

        const response = await this.httpClient.post<{ _id: string, name: string }>(`${this.configService.baseUrl()}user`,
            requestBody, {observe: 'response'}).toPromise();

        return {
            userId: response.body._id,
            authToken: response.headers.get('x-access-token'),
            name: response.body.name
        };
    }

    async requestForEmailVerification(email: string, profileId: string): Promise<void> {
        await this.httpClient.post(`${this.configService.baseUrl()}user/${profileId}/register/email`,
            {email: email}).toPromise();
    }

    async saveEmail(email: string, profileId: string): Promise<void> {
        await this.httpClient.post(`${this.configService.baseUrl()}user/${profileId}/save-email`,
            {email: email}).toPromise();
    }

    async update(user: User): Promise<void> {
        await this.httpClient.put(`${this.configService.baseUrl()}user/${user._id}`, user).toPromise();
    }

    async updateDateOfBirth(userId: string, dateOfBirth: Date): Promise<void> {
        await this.httpClient.patch(`${this.configService.baseUrl()}user/${userId}`, {dateOfBirth: dateOfBirth}).toPromise();
    }

    async markOnboardedBy(userId: string, partnerId: string): Promise<void> {
        await this.httpClient.patch(`${this.configService.baseUrl()}user/${userId}`, {onboardedBy: partnerId}).toPromise();
    }

    async captureAadhaarLinkedWithMobileAnswer(userId: string, aadhaarLinkedWithMobileAnswer: boolean) {
        await this.httpClient.patch(`${this.configService.baseUrl()}user/${userId}`, {
            aadhaarLinkedWithMobile: aadhaarLinkedWithMobileAnswer
        }).toPromise();
    }

    async getUsersByPhoneNumber(phoneNumber: string): Promise<User[]> {
        const url = `${this.configService.baseUrl()}users`;
        const options = {params: new HttpParams().set('phoneNumber', phoneNumber)};
        return this.httpClient.get<User[]>(url, options).toPromise();
    }

    async getUserByPan(pan: string): Promise<User> {
        const url = `${this.configService.baseUrl()}user`;
        const options = {params: new HttpParams().set('pan', pan)};
        return this.httpClient.get<User>(url, options).toPromise();
    }

    async getContactInfosByUserId(userId: string): Promise<ContactInfos> {
        const url = `${this.configService.getUserServiceUrl()}/contact-infos`;
        const options = {params: new HttpParams().set('userId', userId)};
        return this.httpClient.get<ContactInfos>(url, options).toPromise();
    }

    async updatePhoneNumberAndMarkOtpAsVerified(userId: string, phoneNumber: string) {
        const url = `${this.configService.baseUrl()}user/${userId}/phone-number`;
        const requestBody = {phoneNumber: phoneNumber, isOtpVerified: true};
        this.httpClient.put(url, requestBody).toPromise();
    }

    async markContactAsPrimary(userId: string, phoneNumber: string): Promise<ContactInfo> {
        const url = `${this.configService.getUserServiceUrl()}/contact-info/primary`;
        const requestBody = {userId: userId, phoneNumber: phoneNumber};
        return this.httpClient.put<ContactInfo>(url, requestBody).toPromise();
    }

    async getEmailAddressesByUserId(userId: string): Promise<EmailAddresses> {
        const url = `${this.configService.getUserServiceUrl()}/email-addresses`;
        const options = {params: new HttpParams().set('userId', userId)};
        return this.httpClient.get<EmailAddresses>(url, options).toPromise();
    }

    async markEmailAddressAsPrimary(userId: string, emailId: string): Promise<EmailAddress> {
        const url = `${this.configService.getUserServiceUrl()}/email-address/primary`;
        const requestBody = {userId: userId, emailId: emailId};
        return this.httpClient.put<EmailAddress>(url, requestBody).toPromise();
    }

    async getUserByProfileId(userId:string):Promise<any> {
        const url = `${this.configService.getUserServiceUrl()}/user/check-login-profile/${userId}`;
        return this.httpClient.get<any>(url).toPromise();
    }

    async checkPanExists(pan:string):Promise<panExists> {
        const url = `${this.configService.baseUrl()}user/pan-exists`;
        const requestBody = { pan: pan };
        return this.httpClient.post<panExists>(url, requestBody).toPromise();
    }

    async getKarzaPanName(pan:string):Promise<{name: string}> {
        const url = `${this.configService.baseUrl()}misc/karza/getName?pan=${pan}`;
        return this.httpClient.get<{name: string}>(url).toPromise();
    }

    async createContactInfo(contactInfo: any): Promise<any> {
        const url = `${this.configService.baseUrl()}user/${contactInfo.userId}/createContactInfo`;
        return this.httpClient.post<any>(url, contactInfo).toPromise();
    }

    async createOrUpdateCibilContacts(userId: string, contact_id: string): Promise<any> {
        const url = `${this.configService.baseUrl()}user/${userId}/cibil-contact`;
        const requestBody = {
            "contactId": contact_id
        };
        return this.httpClient.put<any>(url, requestBody).toPromise();
    }
}
